/**
 * Regras puras do painel Calendário de Prioridades (testadas em
 * calendarUtils.test.ts). Datas sempre como string YYYY-MM-DD — nunca
 * `new Date(iso)` (regra do repo: sem conversão de fuso).
 */

import { CalendarEvent, CalendarEventType } from '@/types/dashboard';
import { pad2 } from '@components/dateInputUtils';

/** Agrupa os eventos por dia (chave YYYY-MM-DD), preservando a ordem da API. */
export const groupEventsByDay = (events: CalendarEvent[]): Record<string, CalendarEvent[]> => {
  const byDay: Record<string, CalendarEvent[]> = {};
  for (const ev of events) {
    if (!byDay[ev.date]) byDay[ev.date] = [];
    byDay[ev.date].push(ev);
  }
  return byDay;
};

/** Soma `delta` meses a (year, month 0-based), devolvendo o par normalizado. */
export const shiftMonth = (
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } => {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
};

/** Formata (year, month 0-based) como parâmetro YYYY-MM da API. */
export const monthParam = (year: number, month: number): string => `${year}-${pad2(month + 1)}`;

/** Evento de Ordem de Entrega avulsa (pendente ou emergencial). */
export const isGoodsDeliveryEvent = (type: CalendarEventType): boolean => type.startsWith('goods_delivery');

/** Rota de navegação de um evento do calendário. */
export const routeForEvent = (ev: CalendarEvent): string => {
  if (isGoodsDeliveryEvent(ev.type)) return `/goods-delivery-orders/${ev.id}`;
  return ev.type.startsWith('purchase') ? `/purchase-orders/${ev.id}` : `/sales-orders/${ev.id}`;
};

/** Prefixo do número do evento no chip: PC (compra), PV (venda) ou OE (ordem de entrega). */
export const eventRefPrefix = (type: CalendarEventType): string => {
  if (isGoodsDeliveryEvent(type)) return 'OE';
  return type.startsWith('purchase') ? 'PC' : 'PV';
};

/** Rótulo curto do tipo de evento (legenda e tooltip). */
export const eventTypeLabel = (type: CalendarEventType): string => {
  switch (type) {
    case 'purchase_priority':
      return 'Compra prioritária';
    case 'purchase_expected':
      return 'Entrega de fornecedor';
    case 'sales_priority':
      return 'Venda prioritária';
    case 'sales_scheduled_ship':
      return 'Envio agendado';
    case 'goods_delivery_expected':
      return 'Entrega avulsa';
    case 'goods_delivery_emergency':
      return 'Entrega avulsa — emergência';
  }
};

/** Classes Tailwind do chip por tipo de evento. */
export const eventChipClasses = (type: CalendarEventType): string => {
  switch (type) {
    case 'purchase_priority':
      return 'bg-amber-100 text-amber-800 border border-amber-300';
    case 'purchase_expected':
      return 'bg-slate-100 text-slate-600 border border-slate-200';
    case 'sales_priority':
      return 'bg-red-100 text-red-700 border border-red-300';
    case 'sales_scheduled_ship':
      return 'bg-blue-100 text-blue-700 border border-blue-300';
    case 'goods_delivery_expected':
      return 'bg-teal-100 text-teal-800 border border-teal-300';
    case 'goods_delivery_emergency':
      return 'bg-red-600 text-white border border-red-700';
  }
};
